import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ActionReducerMapBuilder } from "@reduxjs/toolkit/src/mapBuilders";
import { NoInfer } from "@reduxjs/toolkit/src/tsHelpers";
import { setUser } from "../../common/user";
import { flatFeeService } from "../../../services/admin/flatFee.service";
import {
	setApproveAdjustmentState,
	setCreateCreativeState,
	setGeneralProgressHide,
	setGeneralProgressShow,
	setGeneralSnackbarState
} from "../../common/ui";
import { PaymentSetData } from "../reports/details_finance_report";
import { detailsFinanceReportService } from "../../../services/admin/details_finance_report.service";


export type payloadSportList = {
	page: string | number | undefined;
	perPage: string | number | undefined;
	filters: {
	  affiliateIds?: [],
	  trackerIds?: [],
	  brandCodes?: [],
	  statuses?: [],
    dateFrom?: string;
    dateTo?: string;
  }
};

export type columnsItem = {
  name: string;
  type: string;
  sort: boolean;
  match?: boolean;
};

export type listItem = {
  [key: string]: any;
};

export interface ITableData {
  list: listItem[];
  columns: columnsItem[];
	totalEntries: number;
	startRowNum: number;
	totalPages: number;
	endRowNum: number;
}

export type PaymentSetData = {
	periodId: number;
	affiliateId: number;
	brand: string;
	currency: string;
	amount: string;
	paymentDate: string,
	transactionId: string | null,
	walletCurrency: string | null,
	walletAmount: string | null,
	walletPaymentMethodName: string | null,
	walletPaymentMethodValue: any | null,
};

export type flatFeeSliceState = {
  list: {
    data: ITableData | null,
    error: any,
    isFetching: boolean,
  },
	deleteFlatFee: {
    data: any,
    error: any,
    isFetching: boolean,
  },
	createFlatFee: {
    data: any,
    error: any,
    isFetching: boolean,
  },
	updateFlatFee: {
    data: any,
    error: any,
    isFetching: boolean,
  },
	download: {
		data: any | null,
		error: any,
		isFetching: boolean,
	},
	payoutsList: {
		data: any,
		error: any,
		isFetching: boolean,
	},
	createPayout: {
		data: any,
		error: any,
		isFetching: boolean,
	},
	updatePayout: {
		data: any,
		error: any,
		isFetching: boolean,
	},
	removePayout: {
		data: any,
		error: any,
		isFetching: boolean,
	},
	paymentChange: {
		data: null,
		error: null,
		isFetching: false,
	},
	availablePaymentsList: {
		data: any,
		error: any,
		isFetching: false,
	},
	availablePayments: {
		data: any,
		error: any,
		isFetching: false,
	},
};

const initialState: flatFeeSliceState = {
  list: {
    data: null,
    error: null,
    isFetching: false,
  },
	deleteFlatFee: {
    data: null,
    error: null,
    isFetching: false,
  },
	createFlatFee: {
    data: null,
    error: null,
    isFetching: false,
  },
	updateFlatFee: {
    data: null,
    error: null,
    isFetching: false,
  },
	download: {
    data: null,
    error: null,
    isFetching: false,
  },
	payoutsList: {
		data: null,
		error: null,
		isFetching: false,
	},
	createPayout: {
		data: null,
		error: null,
		isFetching: false,
	},
	updatePayout: {
		data: null,
		error: null,
		isFetching: false,
	},
	removePayout: {
		data: null,
		error: null,
		isFetching: false,
	},
	paymentChange: {
		data: null,
		error: null,
		isFetching: false,
	},
	availablePaymentsList: {
		data: {},
		error: null,
		isFetching: false,
	},
	availablePayments: {
		data: {},
		error: null,
		isFetching: false,
	},
};

export const getListData: any = createAsyncThunk(
  'admin/flatFee/geListData',
  async (payload: payloadSportList, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await flatFeeService.getList(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const deleteFlatFeeData: any = createAsyncThunk(
	'admin/flatFee/deleteFlatFeeData',
	async (payload: payloadSportList, { rejectWithValue, dispatch }) => {
		dispatch(setGeneralProgressShow());
		try {
			const response = await flatFeeService.deleteFlatFee(payload);
			const data = await response.json();

			if (data.hasOwnProperty('authorized') && data.authorized === false) {
				dispatch(setUser(null));
				localStorage.removeItem('user');
			}

			if (response.ok && response.status === 200) {
				dispatch(
					setGeneralSnackbarState({
						open: true,
						type: 'success',
						message: 'success',
						messageKey: 'common.messages.success',
					})
				);
				dispatch(setCreateCreativeState(false));
			}

			if (!response.ok) {
				dispatch(
					setGeneralSnackbarState({
						open: true,
						type: 'warning',
						message: 'error',
						messageKey: 'common.messages.error',
					})
				);
				dispatch(setGeneralProgressHide());
				return rejectWithValue(data)
			}

			dispatch(setGeneralProgressHide());

			return data;
		} catch (error) {
			dispatch(
				setGeneralSnackbarState({
					open: true,
					type: 'warning',
					message: 'error',
					messageKey: 'common.messages.error',
				})
			);
			dispatch(setGeneralProgressHide());
			return rejectWithValue(error)
		}
	}
);

export const createFlatFeeData: any = createAsyncThunk(
	'admin/flatFee/createFlatFeeData',
	async (payload: payloadSportList, { rejectWithValue, dispatch }) => {
		dispatch(setGeneralProgressShow());
		try {
			const response = await flatFeeService.createFlatFee(payload);
			const data = await response.json();

			if (data.hasOwnProperty('authorized') && data.authorized === false) {
				dispatch(setUser(null));
				localStorage.removeItem('user');
			}

			if (response.ok && response.status === 201) {
				dispatch(
					setGeneralSnackbarState({
						open: true,
						type: 'success',
						message: 'success',
						messageKey: 'common.messages.success',
					})
				);
				dispatch(setCreateCreativeState(false));
			}

			if (!response.ok) {
				dispatch(
					setGeneralSnackbarState({
						open: true,
						type: 'warning',
						message: 'error',
						messageKey: 'common.messages.error',
					})
				);
				dispatch(setGeneralProgressHide());
				return rejectWithValue(data)
			}

			dispatch(setGeneralProgressHide());

			return data;
		} catch (error) {
			dispatch(
				setGeneralSnackbarState({
					open: true,
					type: 'warning',
					message: 'error',
					messageKey: 'common.messages.error',
				})
			);
			dispatch(setGeneralProgressHide());
			return rejectWithValue(error)
		}
	}
);

export const updateFlatFeeData: any = createAsyncThunk(
	'admin/flatFee/updateFlatFeeData',
	async (payload: payloadSportList, { rejectWithValue, dispatch }) => {
		dispatch(setGeneralProgressShow());
		try {
			const response = await flatFeeService.updateFlatFee(payload);
			const data = await response.json();

			if (data.hasOwnProperty('authorized') && data.authorized === false) {
				dispatch(setUser(null));
				localStorage.removeItem('user');
			}

			if (response.ok && response.status === 200) {
				dispatch(
					setGeneralSnackbarState({
						open: true,
						type: 'success',
						message: 'success',
						messageKey: 'common.messages.success',
					})
				);
				dispatch(setCreateCreativeState(false));
			}

			if (!response.ok) {
				dispatch(
					setGeneralSnackbarState({
						open: true,
						type: 'warning',
						message: 'error',
						messageKey: 'common.messages.error',
					})
				);
				dispatch(setGeneralProgressHide());
				return rejectWithValue(data)
			}

			dispatch(setGeneralProgressHide());

			return data;
		} catch (error) {
			dispatch(
				setGeneralSnackbarState({
					open: true,
					type: 'warning',
					message: 'error',
					messageKey: 'common.messages.error',
				})
			);
			dispatch(setGeneralProgressHide());
			return rejectWithValue(error)
		}
	}
);

export const setDownloadData: any = createAsyncThunk(
	'admin/flatFee/setDownloadData',
	async (payload: any, { rejectWithValue, dispatch }) => {
		dispatch(setGeneralProgressShow());
		try {
			const response = await flatFeeService.setDownload(payload);
			const data = await response.json();

			if (data.hasOwnProperty('authorized') && data.authorized === false) {
				dispatch(setUser(null));
				localStorage.removeItem('user');
			}

			if (!response.ok) {
				dispatch(setGeneralProgressHide());
				return rejectWithValue(data)
			}

			dispatch(setGeneralProgressHide());

			return data;
		} catch (error) {
			dispatch(setGeneralProgressHide());
			return rejectWithValue(error)
		}
	}
);

export const getPayoutsData: any = createAsyncThunk(
	'admin/flatFee/getPayoutsData',
	async (payload:any, { rejectWithValue, dispatch }) => {
		dispatch(setGeneralProgressShow());
		try {
			const response = await flatFeeService.getPayouts(payload);
			const data = await response.json();

			if (data.hasOwnProperty('authorized') && data.authorized === false) {
				dispatch(setGeneralProgressHide());
				dispatch(setUser(null));
				localStorage.removeItem('user');
			}

			if (!response.ok) {
				dispatch(setGeneralProgressHide());
				return rejectWithValue(data)
			}

			dispatch(setGeneralProgressHide());
			return {...data, ...(!!payload && payload.hasOwnProperty('convert_currency') ? {rate_currency: payload.convert_currency} : {})};
		} catch (error) {
			dispatch(setGeneralProgressHide());
			return rejectWithValue(error)
		}
	}
);

export const createPayoutData: any = createAsyncThunk(
	'admin/flatFee/createPayoutData',
	async (payload: PaymentSetData, { rejectWithValue, dispatch }) => {
		dispatch(setGeneralProgressShow());
		try {
			const response = await flatFeeService.createPayout(payload);
			const data = await response.json();

			if (response.ok) {
				dispatch(
					setGeneralSnackbarState({
						open: true,
						type: 'success',
						message: 'success',
						messageKey: 'common.messages.success',
					})
				);
				dispatch(setApproveAdjustmentState(false));
			}

			if (!response.ok) {
				dispatch(
					setGeneralSnackbarState({
						open: true,
						type: 'warning',
						message: 'error',
						messageKey: 'common.messages.error',
					})
				);
				dispatch(setGeneralProgressHide());
				return rejectWithValue(data)
			}

			dispatch(setGeneralProgressHide());

			return data;
		} catch (error) {
			dispatch(
				setGeneralSnackbarState({
					open: true,
					type: 'warning',
					message: 'error',
					messageKey: 'common.messages.error',
				})
			);
			dispatch(setGeneralProgressHide());
			return rejectWithValue(error)
		}
	}
);

export const updatePayoutData: any = createAsyncThunk(
	'admin/flatFee/updatePayoutData',
	async (payload: PaymentSetData, { rejectWithValue, dispatch }) => {
		dispatch(setGeneralProgressShow());
		try {
			const response = await flatFeeService.updatePayout(payload);
			const data = await response.json();

			if (response.ok) {
				dispatch(
					setGeneralSnackbarState({
						open: true,
						type: 'success',
						message: 'success',
						messageKey: 'common.messages.success',
					})
				);
				dispatch(setApproveAdjustmentState(false));
			}

			if (!response.ok) {
				dispatch(
					setGeneralSnackbarState({
						open: true,
						type: 'warning',
						message: 'error',
						messageKey: 'common.messages.error',
					})
				);
				dispatch(setGeneralProgressHide());
				return rejectWithValue(data)
			}

			dispatch(setGeneralProgressHide());

			return data;
		} catch (error) {
			dispatch(
				setGeneralSnackbarState({
					open: true,
					type: 'warning',
					message: 'error',
					messageKey: 'common.messages.error',
				})
			);
			dispatch(setGeneralProgressHide());
			return rejectWithValue(error)
		}
	}
);

export const removePayoutData: any = createAsyncThunk(
	'admin/flatFee/removePayoutData',
	async (payload: PaymentSetData, { rejectWithValue, dispatch }) => {
		dispatch(setGeneralProgressShow());
		try {
			const response = await flatFeeService.removePayout(payload);
			const data = await response.json();

			if (response.ok) {
				dispatch(
					setGeneralSnackbarState({
						open: true,
						type: 'success',
						message: 'success',
						messageKey: 'common.messages.success',
					})
				);
				dispatch(setApproveAdjustmentState(false));
			}

			if (!response.ok) {
				dispatch(
					setGeneralSnackbarState({
						open: true,
						type: 'warning',
						message: 'error',
						messageKey: 'common.messages.error',
					})
				);
				dispatch(setGeneralProgressHide());
				return rejectWithValue(data)
			}

			dispatch(setGeneralProgressHide());

			return data;
		} catch (error) {
			dispatch(
				setGeneralSnackbarState({
					open: true,
					type: 'warning',
					message: 'error',
					messageKey: 'common.messages.error',
				})
			);
			dispatch(setGeneralProgressHide());
			return rejectWithValue(error)
		}
	}
);

export const setPaymentData: any = createAsyncThunk(
	'admin/flatFee/payment/change',
	async (payload: PaymentSetData, { rejectWithValue, dispatch }) => {
		dispatch(setGeneralProgressShow());
		try {
			const response = await flatFeeService.setPayment(payload);
			const data = await response.json();

			if (response.ok) {
				dispatch(
					setGeneralSnackbarState({
						open: true,
						type: 'success',
						message: 'success',
						messageKey: 'common.messages.success',
					})
				);
				dispatch(setApproveAdjustmentState(false));
			}

			if (!response.ok) {
				dispatch(
					setGeneralSnackbarState({
						open: true,
						type: 'warning',
						message: 'error',
						messageKey: 'common.messages.error',
					})
				);
				dispatch(setGeneralProgressHide());
				return rejectWithValue(data)
			}

			dispatch(setGeneralProgressHide());

			return data;
		} catch (error) {
			dispatch(
				setGeneralSnackbarState({
					open: true,
					type: 'warning',
					message: 'error',
					messageKey: 'common.messages.error',
				})
			);
			dispatch(setGeneralProgressHide());
			return rejectWithValue(error)
		}
	}
);

export const getAvailablePaymentsData: any = createAsyncThunk(
	'admin/flatFee/getAvailablePaymentsData',
	async (payload: PaymentSetData, { rejectWithValue, dispatch }) => {
		dispatch(setGeneralProgressShow());
		try {
			const response = await flatFeeService.getAvailablePayments(payload);
			const data = await response.json();

			if (!response.ok) {
				return rejectWithValue(data)
			}

			dispatch(setGeneralProgressHide());

			return data;
		} catch (error) {
			return rejectWithValue(error)
		}
	}
);

export const getAvailablePaymentsListData: any = createAsyncThunk(
	'admin/flatFee/getAvailablePaymentsListData',
	async (payload: PaymentSetData, { rejectWithValue, dispatch }) => {
		dispatch(setGeneralProgressShow());
		try {
			const response = await flatFeeService.getAvailablePayments(payload);
			const data = await response.json();

			if (!response.ok) {
				dispatch(setGeneralProgressHide());
				return rejectWithValue(data)
			}

			dispatch(setGeneralProgressHide());

			return data;
		} catch (error) {
			return rejectWithValue(error)
		}
	}
);

export const editPaymentData: any = createAsyncThunk(
	'admin/flatFee/editPaymentData',
	async (payload: PaymentSetData, { rejectWithValue, dispatch }) => {
		dispatch(setGeneralProgressShow());
		try {
			const response = await flatFeeService.editPayment(payload);
			const data = await response.json();

			if (response.ok) {
				dispatch(
					setGeneralSnackbarState({
						open: true,
						type: 'success',
						message: 'success',
						messageKey: 'common.messages.success',
					})
				);
				dispatch(setApproveAdjustmentState(false));
			}

			if (!response.ok) {
				dispatch(
					setGeneralSnackbarState({
						open: true,
						type: 'warning',
						message: 'error',
						messageKey: 'common.messages.error',
					})
				);
				dispatch(setGeneralProgressHide());
				return rejectWithValue(data)
			}

			dispatch(setGeneralProgressHide());

			return data;
		} catch (error) {
			dispatch(
				setGeneralSnackbarState({
					open: true,
					type: 'warning',
					message: 'error',
					messageKey: 'common.messages.error',
				})
			);
			dispatch(setGeneralProgressHide());
			return rejectWithValue(error)
		}
	}
);

export const flatFeeSlice = createSlice({
  name: 'flatFee',
  initialState: initialState,
  reducers: {
    clearListState(state) {
      state.list.error = null;
      state.list.isFetching = false;
      state.list.data = null;
    },
	  clearDeleteState(state) {
      state.deleteFlatFee.error = null;
      state.deleteFlatFee.isFetching = false;
      state.deleteFlatFee.data = null;
    },
	  clearCreateFlatFeeState(state) {
      state.createFlatFee.error = null;
      state.createFlatFee.isFetching = false;
      state.createFlatFee.data = null;
    },
	  clearUpdateFlatFeeState(state) {
      state.updateFlatFee.error = null;
      state.updateFlatFee.isFetching = false;
      state.updateFlatFee.data = null;
    },
	  clearDownload(state) {
		  state.download.data = null;
		  state.download.error = null;
		  state.download.isFetching = false;
	  },
	  clearPayoutsListData(state) {
		  state.payoutsList.data = null;
		  state.payoutsList.error = null;
		  state.payoutsList.isFetching = false;
	  },
	  clearEditPaymentData(state) {
		  state.editPayment.data = null;
		  state.editPayment.error = null;
		  state.editPayment.isFetching = false;
	  },
	  clearCreatePayoutData(state) {
		  state.createPayout.data = null;
		  state.createPayout.error = null;
		  state.createPayout.isFetching = false;
	  },
	  clearUpdatePayoutData(state) {
		  state.updatePayout.data = null;
		  state.updatePayout.error = null;
		  state.updatePayout.isFetching = false;
	  },
	  clearRemovePayoutData(state) {
		  state.removePayout.data = null;
		  state.removePayout.error = null;
		  state.removePayout.isFetching = false;
	  },
	  clearPaymentData(state) {
		  state.paymentChange.data = null;
		  state.paymentChange.error = null;
		  state.paymentChange.isFetching = false;
	  },
	  clearAvailablePaymentsData(state) {
		  state.availablePayments.data = null;
		  state.availablePayments.error = null;
		  state.availablePayments.isFetching = false;
	  },
	  clearAvailablePaymentsListData(state) {
		  state.availablePaymentsList.data = {};
		  state.availablePaymentsList.error = null;
		  state.availablePaymentsList.isFetching = false;
	  },
  },
  extraReducers: (builder: ActionReducerMapBuilder<NoInfer<flatFeeSliceState>>) => {
    builder.addCase(getListData.pending, (state ) => {
      state.list.isFetching = true;
      state.list.error = null;
    });
    builder.addCase(getListData.fulfilled, (state , action) => {
      state.list.error = null;
      state.list.isFetching = false;
	    state.list.data = action.payload;
    });
    builder.addCase(getListData.rejected, (state , action) => {
      state.list.isFetching = false;
      state.list.error = action.payload;
      state.list.data = null;
    });

		builder.addCase(deleteFlatFeeData.pending, (state ) => {
      state.deleteFlatFee.isFetching = true;
      state.deleteFlatFee.error = null;
    });
    builder.addCase(deleteFlatFeeData.fulfilled, (state , action) => {
      state.deleteFlatFee.error = null;
      state.deleteFlatFee.isFetching = false;
	    state.deleteFlatFee.data = action.payload;
    });
    builder.addCase(deleteFlatFeeData.rejected, (state , action) => {
      state.deleteFlatFee.isFetching = false;
      state.deleteFlatFee.error = action.payload;
      state.deleteFlatFee.data = null;
    });

		builder.addCase(createFlatFeeData.pending, (state ) => {
      state.createFlatFee.isFetching = true;
      state.createFlatFee.error = null;
    });
    builder.addCase(createFlatFeeData.fulfilled, (state , action) => {
      state.createFlatFee.error = null;
      state.createFlatFee.isFetching = false;
	    state.createFlatFee.data = action.payload;
    });
    builder.addCase(createFlatFeeData.rejected, (state , action) => {
      state.createFlatFee.isFetching = false;
      state.createFlatFee.error = action.payload;
      state.createFlatFee.data = null;
    });

		builder.addCase(updateFlatFeeData.pending, (state ) => {
      state.updateFlatFee.isFetching = true;
      state.updateFlatFee.error = null;
    });
    builder.addCase(updateFlatFeeData.fulfilled, (state , action) => {
      state.updateFlatFee.error = null;
      state.updateFlatFee.isFetching = false;
	    state.updateFlatFee.data = action.payload;
    });
    builder.addCase(updateFlatFeeData.rejected, (state , action) => {
      state.updateFlatFee.isFetching = false;
      state.updateFlatFee.error = action.payload;
      state.updateFlatFee.data = null;
    });

	  builder.addCase(setDownloadData.pending, (state ) => {
		  state.download.isFetching = true;
		  state.download.error = null;
	  });
	  builder.addCase(setDownloadData.fulfilled, (state , action) => {
		  state.download.error = null;
		  state.download.isFetching = false;
		  state.download.data = action.payload;
	  });
	  builder.addCase(setDownloadData.rejected, (state , action) => {
		  state.download.isFetching = false;
		  state.download.error = action.payload;
	  });

	  builder.addCase(getPayoutsData.pending, (state ) => {
		  state.payoutsList.isFetching = true;
		  state.payoutsList.error = null;
	  });
	  builder.addCase(getPayoutsData.fulfilled, (state, action ) => {
		  state.payoutsList.error = null;
		  state.payoutsList.isFetching = false;
		  state.payoutsList.data = action.payload;
	  });
	  builder.addCase(getPayoutsData.rejected, (state, action ) => {
		  state.payoutsList.data = null;
		  state.payoutsList.isFetching = false;
		  state.payoutsList.error = action.payload;
	  });

	  builder.addCase(createPayoutData.pending, (state ) => {
		  state.createPayout.isFetching = true;
		  state.createPayout.error = null;
	  });
	  builder.addCase(createPayoutData.fulfilled, (state, action ) => {
		  state.createPayout.error = null;
		  state.createPayout.isFetching = false;
		  state.createPayout.data = action.payload;
	  });
	  builder.addCase(createPayoutData.rejected, (state, action ) => {
		  state.createPayout.data = null;
		  state.createPayout.isFetching = false;
		  state.createPayout.error = action.payload;
	  });

	  builder.addCase(updatePayoutData.pending, (state ) => {
		  state.updatePayout.isFetching = true;
		  state.updatePayout.error = null;
	  });
	  builder.addCase(updatePayoutData.fulfilled, (state, action ) => {
		  state.updatePayout.error = null;
		  state.updatePayout.isFetching = false;
		  state.updatePayout.data = action.payload;
	  });
	  builder.addCase(updatePayoutData.rejected, (state, action ) => {
		  state.updatePayout.data = null;
		  state.updatePayout.isFetching = false;
		  state.updatePayout.error = action.payload;
	  });

	  builder.addCase(removePayoutData.pending, (state ) => {
		  state.removePayout.isFetching = true;
		  state.removePayout.error = null;
	  });
	  builder.addCase(removePayoutData.fulfilled, (state, action ) => {
		  state.removePayout.error = null;
		  state.removePayout.isFetching = false;
		  state.removePayout.data = action.payload;
	  });
	  builder.addCase(removePayoutData.rejected, (state, action ) => {
		  state.removePayout.data = null;
		  state.removePayout.isFetching = false;
		  state.removePayout.error = action.payload;
	  });

	  builder.addCase(setPaymentData.pending, (state ) => {
		  state.paymentChange.isFetching = true;
		  state.paymentChange.error = null;
	  });
	  builder.addCase(setPaymentData.fulfilled, (state, action ) => {
		  state.paymentChange.error = null;
		  state.paymentChange.isFetching = false;
		  state.paymentChange.data = action.payload;
	  });
	  builder.addCase(setPaymentData.rejected, (state, action ) => {
		  state.paymentChange.error = action.payload;
	  });

	  builder.addCase(getAvailablePaymentsListData.pending, (state ) => {
		  state.availablePaymentsList.isFetching = true;
		  state.availablePaymentsList.error = null;
	  });
	  builder.addCase(getAvailablePaymentsListData.fulfilled, (state, action ) => {
		  state.availablePaymentsList.error = null;
		  state.availablePaymentsList.isFetching = false;
		  const { affiliateId } = action.meta.arg;
		  state.availablePaymentsList.data[affiliateId] =  action.payload;
	  });
	  builder.addCase(getAvailablePaymentsListData.rejected, (state, action ) => {
		  state.availablePaymentsList.data = null;
		  state.availablePaymentsList.isFetching = false;
		  state.availablePaymentsList.error = action.payload;
	  });

	  builder.addCase(editPaymentData.pending, (state ) => {
		  state.editPayment.isFetching = true;
		  state.editPayment.error = null;
	  });
	  builder.addCase(editPaymentData.fulfilled, (state, action ) => {
		  state.editPayment.error = null;
		  state.editPayment.isFetching = false;
		  state.editPayment.data = action.payload;
	  });
	  builder.addCase(editPaymentData.rejected, (state, action ) => {
		  state.editPayment.data = null;
		  state.editPayment.isFetching = false;
		  state.editPayment.error = action.payload;
	  });

	  builder.addCase(getAvailablePaymentsData.pending, (state ) => {
		  state.availablePayments.isFetching = true;
		  state.availablePayments.error = null;
	  });
	  builder.addCase(getAvailablePaymentsData.fulfilled, (state, action ) => {
		  state.availablePayments.error = null;
		  state.availablePayments.isFetching = false;
		  state.availablePayments.data = action.payload;
	  });
	  builder.addCase(getAvailablePaymentsData.rejected, (state, action ) => {
		  state.availablePayments.data = null;
		  state.availablePayments.isFetching = false;
		  state.availablePayments.error = action.payload;
	  });
  },
});
export const {
  clearListState,
	clearDeleteState,
	clearCreateFlatFeeState,
	clearUpdateFlatFeeState,
	clearDownload,
	clearPayoutsListData,
	clearCreatePayoutData,
	clearUpdatePayoutData,
	clearRemovePayoutData,
	clearPaymentData,
	clearAvailablePaymentsData,
	clearEditPaymentData,
	clearAvailablePaymentsListData,
} = flatFeeSlice.actions;
export default flatFeeSlice.reducer;
